// useOwlSystemTrackRecord — system-level OWL HOUR track record.
//
// Reads owl_hour_slips (authenticated SELECT is open per
// 20260509223308_owl_hour_authenticated_read.sql) and dedupes per
// (slip_date, tier). The pipeline writes one row per (user_id,
// slip_date, tier) but the slip body (legs/odds/result) is identical
// across users — so DISTINCT collapses to the system-published slip.
//
// Window: last 30 days of slips. Result column was settled by the
// owl-settle-results cron; we honestly render whatever's there (NULL
// = pending). No fabrication.

import { useEffect, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { OwlTier, OwlResult, OwlLeg } from "./useOwlHourSlips.ts";

export interface OwlSystemSlip {
  slip_date: string;
  refresh_phase?: string;
  tier: OwlTier;
  combined_odds: number | null;
  confidence: number | null;
  legs: readonly OwlLeg[];
  legs_count: number;
  leagues_covered: readonly string[];
  result: OwlResult | null;
  result_ready_at: string | null;
  status: string;
  meta: Record<string, unknown>;
}

export interface OwlTrackSummary {
  total_nights: number;
  total_slips: number;          // up to 5x total_nights
  wins: number;
  losses: number;
  partials: number;
  voids: number;
  pending: number;
  hit_rate: number | null;       // wins / (wins+losses+partials) — null if no settled
  unit_roi: number | null;
  roi_sample: number;
}

export interface UseOwlSystemTrackRecordState {
  slips: readonly OwlSystemSlip[];
  summary: OwlTrackSummary;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_SUMMARY: OwlTrackSummary = {
  total_nights: 0, total_slips: 0,
  wins: 0, losses: 0, partials: 0, voids: 0, pending: 0,
  hit_rate: null,
  unit_roi: null, roi_sample: 0,
};

function dedupeBySlipKey(rows: readonly OwlSystemSlip[]): OwlSystemSlip[] {
  // Same (slip_date, tier) emits one row per user — collapse to one.
  // Prefer the settled row (result != null) when present.
  const seen = new Map<string, OwlSystemSlip>();
  for (const r of rows) {
    const key = `${r.slip_date}|${r.refresh_phase}|${r.tier}`;
    const prev = seen.get(key);
    if (!prev || (r.result != null && prev.result == null)) {
      seen.set(key, r);
    }
  }
  return [...seen.values()].sort((a, b) => b.slip_date.localeCompare(a.slip_date));
}

function summarise(slips: readonly OwlSystemSlip[]): OwlTrackSummary {
  if (slips.length === 0) return EMPTY_SUMMARY;
  let wins = 0, losses = 0, partials = 0, voids = 0, pending = 0;
  const nights = new Set<string>();
  for (const s of slips) {
    nights.add(s.slip_date);
    switch (s.result) {
      case "win":     wins++; break;
      case "loss":    losses++; break;
      case "partial": partials++; break;
      case "void":    voids++; break;
      default:        pending++;
    }
  }
  const settled = wins + losses + partials;
  const returns = slips.filter((slip) => slip.result !== null && slip.result !== "void")
    .filter((slip) => slip.meta.settled_odds != null && slip.meta.settled_odds !== "")
    .map((slip) => Number(slip.meta.settled_odds))
    .filter((value) => Number.isFinite(value));
  return {
    total_nights: nights.size,
    total_slips: slips.length,
    wins, losses, partials, voids, pending,
    hit_rate: settled > 0 ? wins / settled : null,
    unit_roi: returns.length >= 30 ? returns.reduce((sum, value) => sum + value - 1, 0) / returns.length : null,
    roi_sample: returns.length,
  };
}

export function useOwlSystemTrackRecord(days = 30): UseOwlSystemTrackRecordState {
  const { user } = useAuthSession();
  const [slips, setSlips] = useState<readonly OwlSystemSlip[]>([]);
  const [summary, setSummary] = useState<OwlTrackSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!user) {
      setSlips([]);
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const since = new Date();
      since.setUTCDate(since.getUTCDate() - days);
      const sinceStr = since.toISOString().slice(0, 10);

      // owl_hour_slips isn't in the generated typings yet — cast through
      // `as never` to bypass the typed client, same pattern as
      // useOwlHourSlips.
      const { data, error: e } = await (client.rpc as any)("get_owl_publications", { p_since: sinceStr });
      if (cancelled) return;
      if (e) {
        setError(e.message);
        setLoading(false);
        return;
      }

      const parsed: OwlSystemSlip[] = (data ?? []).map((r: any) => ({
        slip_date: String(r.slip_date ?? ""),
        refresh_phase: String(r.refresh_phase ?? "evening"),
        tier: (r.tier ?? "conservative") as OwlTier,
        combined_odds: r.combined_odds != null ? Number(r.combined_odds) : null,
        confidence: r.confidence != null ? Number(r.confidence) : null,
        legs: Array.isArray(r.legs) ? r.legs : [],
        legs_count: Array.isArray(r.legs) ? r.legs.length : 0,
        leagues_covered: Array.isArray(r.leagues_covered) ? r.leagues_covered : [],
        result: r.result ?? null,
        result_ready_at: r.result_ready_at ?? null,
        status: String(r.status ?? "draft"),
        meta: (r.meta && typeof r.meta === "object") ? r.meta : {},
      }));

      const deduped = dedupeBySlipKey(parsed);
      setSlips(deduped);
      setSummary(summarise(deduped));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, days, tick]);

  return {
    slips,
    summary,
    loading,
    error,
    refetch: () => setTick((t) => t + 1),
  };
}
